import * as twgl from "twgl.js";
import shaders from "./shaders";
import { Camera } from "./camera";
import { SvtManager } from "./svtManager";

// Manages WebGL rendering, including shaders, buffers, and tile detection.
export class Renderer {
    private gl: WebGL2RenderingContext;
    private mainProgram: twgl.ProgramInfo;
    private detectionProgram: twgl.ProgramInfo;
    private quadBufferInfo: twgl.BufferInfo;
    private detectionTexture: WebGLTexture;
    private detectionFramebuffer: WebGLFramebuffer;
    private pixels: Float32Array;
    private detectionWidth: number;
    private detectionHeight: number;
    private fontTexture: WebGLTexture;
    tileSize: number;
    quadSize: number;
    svtDepth: number;
    svtDetectionFactor: number = 8;
    svtDetectionScaleFactor: number = -Math.log2(this.svtDetectionFactor);
    svtResolution: [number, number];
    visibleTilesGrid: boolean[][][] = [];

    constructor(gl: WebGL2RenderingContext, quadSize: number, tileSize: number, svtDepth: number, svtResolution: [number, number]) {
        this.gl = gl;
        this.mainProgram = twgl.createProgramInfo(gl, [shaders.vert, shaders.frag]);
        this.detectionProgram = twgl.createProgramInfo(gl, [shaders.vert, shaders.detectFrag]);
        this.quadSize = quadSize
        this.tileSize = tileSize
        this.svtDepth = svtDepth
        this.svtResolution = svtResolution
        const halfQuadSize = quadSize / 2;
        this.quadBufferInfo = twgl.createBufferInfoFromArrays(gl, {
            a_position: {
                numComponents: 2,
                data: [-halfQuadSize, -halfQuadSize, halfQuadSize, -halfQuadSize, halfQuadSize, halfQuadSize, -halfQuadSize, halfQuadSize],
            },
            a_uv: { numComponents: 2, data: [0, 0, 1, 0, 1, 1, 0, 1] },
            indices: [0, 1, 2, 0, 2, 3],
        });

        // Visible tile detection
        this.detectionWidth = Math.floor(gl.canvas.width / this.svtDetectionFactor);
        this.detectionHeight = Math.floor(gl.canvas.height / this.svtDetectionFactor);
        this.pixels = new Float32Array(this.detectionWidth * this.detectionHeight * 4);
        this.detectionTexture = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, this.detectionTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, this.detectionWidth, this.detectionHeight, 0, gl.RGBA, gl.FLOAT, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        this.detectionFramebuffer = gl.createFramebuffer()!;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.detectionFramebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.detectionTexture, 0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        this.fontTexture = this.createFontTexture();

        for (let level = 0; level <= svtDepth; level++) {
            const size = 1 << level;
            const col: boolean[][] = [];
            for (let j = 0; j < size; j++) {
                const row: boolean[] = new Array(size).fill(false);
                col.push(row);
            }
            this.visibleTilesGrid.push(col);
        }
    }

    detectVisibleTiles(camera: Camera): Set<string> {
        const { gl, quadBufferInfo, detectionProgram, detectionFramebuffer, detectionWidth, detectionHeight, svtDepth, svtResolution, pixels } = this;

        gl.bindFramebuffer(gl.FRAMEBUFFER, detectionFramebuffer);
        gl.viewport(0, 0, detectionWidth, detectionHeight);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(detectionProgram.program);
        twgl.setBuffersAndAttributes(gl, detectionProgram, quadBufferInfo);
        twgl.setUniforms(detectionProgram, {
            u_matrix: camera.getViewProjectionMat(),
            u_svtDepth: svtDepth,
            u_svtDetectionScaleFactor: this.svtDetectionScaleFactor,
            u_svtResolution: svtResolution,
        });
        twgl.drawBufferInfo(gl, quadBufferInfo);

        gl.readPixels(0, 0, detectionWidth, detectionHeight, gl.RGBA, gl.FLOAT, pixels);

        const visibleTiles = new Set<string>();

        // mark visible tiles
        for (let i = 0; i < pixels.length; i += 4) {
            const level = Math.floor(pixels[i]);
            const col = Math.floor(pixels[i + 1]);
            const row = Math.floor(pixels[i + 2]);
            const valid = pixels[i + 3];
            if (valid > 0.5) {
                this.visibleTilesGrid[level][col][row] = true;
                // NOTE: probably faster than creating a string for each pixel and adding it to the set
                // visibleTiles.add(`level${level}/tx_${col}_${row}`);
            }
        }

        // collect visible tiles
        for (let level = 0; level <= svtDepth; level++) {
            const size = 1 << level;
            for (let col = 0; col < size; col++) {
                for (let row = 0; row < size; row++) {
                    if (this.visibleTilesGrid[level][col][row]) {
                        visibleTiles.add(`level${level}/tx_${col}_${row}`);
                        this.visibleTilesGrid[level][col][row] = false;
                    }
                }
            }
        }

        return visibleTiles;
    }

    draw(camera: Camera, svtManager: SvtManager, debugMode: boolean, hoveredPixel: number[]): void {
        const { gl, mainProgram, quadBufferInfo, fontTexture, tileSize, svtDepth, svtResolution } = this;

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
        // gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(mainProgram.program);
        twgl.setBuffersAndAttributes(gl, mainProgram, quadBufferInfo);
        twgl.setUniforms(mainProgram, {
            u_matrix: camera.getViewProjectionMat(),
            u_svtCacheTexture: svtManager.getAtlasTexture(),
            u_indirectionTable: svtManager.getIndirectionTable(),
            u_fontTexture: fontTexture,
            u_tileSize: tileSize,
            u_svtDepth: svtDepth,
            u_svtResolution: svtResolution,
            u_cacheSize: [svtManager.getAtlasSize(), svtManager.getAtlasSize()],
            u_mouse: hoveredPixel,
            u_quadSize: [this.quadSize, this.quadSize],
            u_gridSize: [1024, 1024],
            u_debug: debugMode,
        });
        twgl.drawBufferInfo(gl, quadBufferInfo);
    }

    // font texture for LOD text rendering
    private createFontTexture(): WebGLTexture {
        const gl = this.gl;
        const canvas = document.createElement("canvas");
        canvas.width = 256;
        canvas.height = 16;
        const ctx = canvas.getContext("2d")!;
        ctx.font = "16px Arial";
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        for (let i = 0; i < 10; i++) {
            ctx.fillText(i.toString(), i * 16 + 8, 8);
        }
        const texture = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return texture;
    }
}